import { immer } from "zustand/middleware/immer";
import { produce } from "immer";
import { create } from "zustand";
import { PreProcessedData } from "@/api/studio/data/types/pre-processed-data.type";
import { Point } from "@/api/studio/data/types/point.type";
import { Region } from "@/api/studio/data/types/region.type";
import { Classification } from "@/api/studio/data/types/classification.type";
import { normalizeAssets, normalizeRegions } from "../utils/region-priority";

/* Store Types */
export interface EditorState {
  assetIndex: number;
  selectedRegion: string | null;
  sidePanelDisplay: Region[];
  allAssetRegionData: PreProcessedData[];
  contextPackIds: string[];
  editorActions: {
    setAssetIndex: (index: number) => void;
    setSelectedRegion: (id: string | null) => void;
    setAllAssetRegionData: (regions: PreProcessedData[]) => void;
    addRegion: (region: Region) => void;
    deleteRegion: (id: string) => void;
    deleteAllRegions: () => void;
    updateRegion: (id: string, points: Point[]) => void;
    updateRegionLabel: (id: string, name: string) => void;
    updateRegionClassification: (id: string, classification: Classification) => void;
    updateRegionColor: (id: string, color: string) => void;
    setContextPackIds: (contextPackIds: string[]) => void;
    reset: () => void;
  };
}

/*
 * Editor slice contains the editor state and actions
 */
export const useEditorStore = create<EditorState>()(
  immer((set) => ({
    assetIndex: 0,
    selectedRegion: null,
    sidePanelDisplay: [],
    allAssetRegionData: [],
    contextPackIds: [],

    /* Actions */
    editorActions: {
      setAssetIndex: (index: number) =>
        set((state) => {
          const regions = normalizeRegions(state.allAssetRegionData[index]?.regions) || [];
          return {
            assetIndex: index,
            // on asset change, update the side panel display to show the regions for the new asset
            sidePanelDisplay: regions && regions.length > 0 ? regions : [],
            // set selected region to null
            selectedRegion: null,
          };
        }),
      setSelectedRegion: (id: string | null) =>
        set((state) =>
          produce(state, (draft) => {
            if (id) {
              // set selected region
              draft.selectedRegion = id;
              // move the selected region to the bottom of the list
              // so it is rendered on top
              if (draft.allAssetRegionData[state.assetIndex].regions) {
                const regionIndex = draft.allAssetRegionData[state.assetIndex].regions!.findIndex((region) => region.id === id);
                const region = draft.allAssetRegionData[state.assetIndex].regions!.splice(regionIndex, 1);
                draft.allAssetRegionData[state.assetIndex].regions!.push(region[0]);
              }
            }
          }),
        ),
      setAllAssetRegionData: (allAssetRegions: PreProcessedData[]) =>
        set((state) => {
          const normalizedAssets = normalizeAssets(allAssetRegions);
          return {
            allAssetRegionData: normalizedAssets,
            sidePanelDisplay: normalizeRegions(normalizedAssets[state.assetIndex]?.regions) || [],
          };
        }),
      addRegion: (region: Region) =>
        set((state) =>
          produce(state, (draft) => {
            const normalizedRegion = { ...region, priority: region.priority ?? 0 };

            // Initialize regions array if it doesn't exist (for scene studies)
            if (!draft.allAssetRegionData[state.assetIndex].regions) {
              draft.allAssetRegionData[state.assetIndex].regions = [];
            }

            draft.allAssetRegionData[state.assetIndex].regions!.push(normalizedRegion);
            draft.sidePanelDisplay.push(normalizedRegion);

            // set the new region as selected
            draft.selectedRegion = normalizedRegion.id;
          }),
        ),
      deleteRegion: (id: string) =>
        set((state) =>
          produce(state, (draft) => {
            // filter out the region
            if (draft.allAssetRegionData[state.assetIndex].regions) {
              draft.allAssetRegionData[state.assetIndex].regions = draft.allAssetRegionData[state.assetIndex].regions!.filter((region) => region.id !== id);
            }

            // keep the side panel display in sync
            draft.sidePanelDisplay = draft.sidePanelDisplay.filter((region) => region.id !== id);
          }),
        ),
      deleteAllRegions: () =>
        set((state) =>
          produce(state, (draft) => {
            draft.allAssetRegionData[state.assetIndex].regions = [];
            draft.sidePanelDisplay = [];
          }),
        ),
      updateRegion: (id: string, points: Point[]) =>
        set((state) =>
          produce(state, (draft) => {
            const asset = draft.allAssetRegionData[state.assetIndex];
            if (asset.regions) {
              const region = asset.regions.find((region) => region.id === id);
              if (region) {
                region.points = points;
              }
            }
          }),
        ),
      updateRegionLabel: (id: string, name: string) =>
        set((state) =>
          produce(state, (draft) => {
            if (draft.allAssetRegionData[state.assetIndex].regions) {
              const region = draft.allAssetRegionData[state.assetIndex].regions!.find((region) => region.id === id);
              if (region) {
                region.label = name;
              }
            }

            // keep the side panel display in sync
            const sidePanelRegion = draft.sidePanelDisplay.find((region) => region.id === id);
            if (sidePanelRegion) {
              sidePanelRegion.label = name;
            }
          }),
        ),
      updateRegionClassification: (id: string, classification: Classification) =>
        set((state) =>
          produce(state, (draft) => {
            if (draft.allAssetRegionData[state.assetIndex].regions) {
              const region = draft.allAssetRegionData[state.assetIndex].regions!.find((region) => region.id === id);
              if (region) {
                region.classification = classification;
                region.label = classification;
              }
            }

            // keep the side panel display in sync
            const sidePanelRegion = draft.sidePanelDisplay.find((region) => region.id === id);
            if (sidePanelRegion) {
              sidePanelRegion.classification = classification;
              sidePanelRegion.label = classification;
            }
          }),
        ),
      updateRegionColor: (id: string, color: string) =>
        set((state) =>
          produce(state, (draft) => {
            if (draft.allAssetRegionData[state.assetIndex].regions) {
              const region = draft.allAssetRegionData[state.assetIndex].regions!.find((region) => region.id === id);
              if (region) {
                region.color = color;
              }
            }

            // keep the side panel display in sync
            const sidePanelRegion = draft.sidePanelDisplay.find((region) => region.id === id);
            if (sidePanelRegion) {
              sidePanelRegion.color = color;
            }
          }),
        ),
      setContextPackIds: (contextPackIds: string[]) => set({ contextPackIds: contextPackIds }),
      reset: () =>
        set({
          assetIndex: 0,
          selectedRegion: null,
          sidePanelDisplay: [],
          allAssetRegionData: [],
          contextPackIds: [],
        }),
    },
  })),
);
